import { Button, Flex } from '@chakra-ui/react';
import { useState } from 'react';
import { ReleaseExport, ReleaseType, TuneCoreRelease } from 'types/releases';
import { DDL } from 'types/general.types';
import { Link } from 'types/api.types';
import { AccountConnectionProviderId, Connection } from 'types/profile';
import { useAppDispatch } from 'app/store';
import { fetchReleaseVersionsDetailsEffect } from 'app/store/Releases/effects';
import {
  useExportTuneCoreFormValidations,
  useFetchDefaultValues,
} from './hooks';
import { ExportTuneCoreReleaseDialogComponent } from './ExportTuneCoreReleaseDialogComponent';
import { ExportTuneCoreReleaseFormValues } from './types';
import { TuneCoreExportStatus } from '../TuneCoreExportStatus';

interface ExportTuneCoreReleaseDialogProps {
  release: TuneCoreRelease;
  genresDDL: DDL;
  releaseExportLink?: Link;
  connection?: Connection;
  exportRelease: (
    data: ExportTuneCoreReleaseFormValues,
    connectionGuid?: string
  ) => Promise<ReleaseExport>;
  isDisabled: boolean;
  fetchAccountConnection: () => Promise<Connection[]>;
  checkConnectionStatus: (
    providerId: AccountConnectionProviderId
  ) => Promise<string | undefined>;
  afterExport: (
    providerId: AccountConnectionProviderId,
    exportGuid: string,
    connectionGuid?: string
  ) => void;
}

export const ExportTuneCoreReleaseDialog = ({
  release,
  genresDDL,
  releaseExportLink,
  connection,
  exportRelease,
  isDisabled,
  fetchAccountConnection,
  checkConnectionStatus,
  afterExport,
}: ExportTuneCoreReleaseDialogProps) => {
  const dispatch = useAppDispatch();
  const [exportDisabled, setExportDisabled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const { validations, genres, languages } = useExportTuneCoreFormValidations({
    releaseExportLink,
    genresDDL,
  });

  const { fetchDefaultValues, defaultValues } = useFetchDefaultValues({
    release,
    genres,
    languages,
  });

  const handleOpen = async () => {
    setExportDisabled(true);
    await Promise.allSettled([
      fetchAccountConnection(),
      fetchDefaultValues(),
      dispatch(fetchReleaseVersionsDetailsEffect()),
    ]);
    setIsOpen(true);
    setExportDisabled(false);
  };

  return (
    <Flex gap={3}>
      <Button
        size="sm"
        isDisabled={exportDisabled || isDisabled}
        onClick={handleOpen}
      >
        Export release
      </Button>
      {isOpen && (
        <ExportTuneCoreReleaseDialogComponent
          setIsOpen={setIsOpen}
          release={release}
          defaultValues={defaultValues}
          genres={genres}
          languages={languages}
          validations={validations}
          connection={connection}
          exportRelease={exportRelease}
          checkConnectionStatus={checkConnectionStatus}
          afterExport={afterExport}
        />
      )}
      {release.type === ReleaseType.TUNECORE && (
        <TuneCoreExportStatus release={release as TuneCoreRelease} />
      )}
    </Flex>
  );
};
